import ArticleIcon from "@mui/icons-material/Article";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Box, Divider, IconButton, Modal, Typography } from "@mui/material";

import StatCard from "./StatCard";
import ScoreIcon from "../../../icons/ScoreIcon";

interface SocialInsightDisplay {
  category: string;
  views: number | string;
  posts: number | string;
  dataInsight: string;
  audienceTrend: string;
  action: string;
  relevanceScore: string;
  relevanceAssessment: string;
  num_of_articles?: number | string;
  view_count?: number | string;
  top_5_countries?: string;
  mode_agerange?: string;
  Female?: number | string;
  Male?: number | string;
  PreferNotToDisclose?: number | string;
  average_viewed_percentage?: number | string;
  average_viewed_duration_sec?: number | string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  categoryDataList: SocialInsightDisplay[];
}

const DISPLAY_DASH = "-";

const isNaNString = (v: unknown) =>
  typeof v === "string" && v.trim().toLowerCase() === "nan";

const isBlankish = (v: unknown) =>
  v === null ||
  v === undefined ||
  (typeof v === "string" && (v.trim() === "" || isNaNString(v)));

const safeText = (v: unknown, dash: string = DISPLAY_DASH) => {
  if (isBlankish(v)) return dash;
  return String(v);
};

const toNumberOrNaN = (v: unknown): number => {
  if (isBlankish(v)) return NaN;
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const n = Number(v);
    return Number.isNaN(n) ? NaN : n;
  }
  return NaN;
};

const formatNumber = (input: unknown): string => {
  const num = toNumberOrNaN(input);
  if (Number.isNaN(num)) return DISPLAY_DASH;

  if (num >= 1_000_000_000) {
    const value = (num / 1_000_000_000).toFixed(1);
    return value.endsWith(".0") ? value.slice(0, -2) + "B" : value + "B";
  }
  if (num >= 1_000_000) {
    const value = (num / 1_000_000).toFixed(1);
    return value.endsWith(".0") ? value.slice(0, -2) + "M" : value + "M";
  }
  if (num >= 1_000) {
    const value = (num / 1_000).toFixed(1);
    return value.endsWith(".0") ? value.slice(0, -2) + "K" : value + "K";
  }
  return String(num);
};

const formatPercent = (input?: unknown): string => {
  const num = toNumberOrNaN(input);
  if (Number.isNaN(num)) return DISPLAY_DASH;
  return `${num}%`;
};

const formatSeconds = (input?: unknown): string => {
  const num = toNumberOrNaN(input);
  if (Number.isNaN(num)) return DISPLAY_DASH;
  return `${num}s`;
};

const OmneInsightsModal = ({ open, onClose, title, categoryDataList }: Props) => {
  return (
    <Modal open={open} onClose={onClose} aria-labelledby="omne-insights-modal-title">
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "90%", sm: "90%", md: "70%" },
          height: { xs: "90%", sm: "90%", md: "90%" },
          maxHeight: "90vh",
          bgcolor: "background.paper",
          borderRadius: "16px",
          boxShadow: 24,
          p: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 3,
            borderBottom: "1px solid #e0e0e0",
            background: "#E77825",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography fontWeight="600" fontSize="24px" color="white">
              {title}
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: "text.secondary" }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ p: 4, overflowY: "auto", flex: 1, bgcolor: "#fafbfc" }}>
          {categoryDataList.map((item, idx) => (
            <Box key={`${item.category}-${idx}`} sx={{ mb: 4 }}>
              <Typography fontWeight={700} fontSize="18px" sx={{ mb: 2 }}>
                {item.category}
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(5, 1fr)",
                  gap: 2.5,
                  mb: 4,
                }}
              >
                <StatCard
                  icon={<VisibilityIcon sx={{ fontSize: 36, color: "#E87722" }} />}
                  value={formatNumber(item.view_count ?? item.views)}
                  label="Total Views"
                />
                <StatCard
                  icon={<ArticleIcon sx={{ fontSize: 36, color: "#E87722" }} />}
                  value={formatNumber(item.num_of_articles ?? item.posts)}
                  label="Total Posts"
                />
                <StatCard
                  icon={<ScoreIcon />}
                  value={formatPercent(item.average_viewed_percentage)}
                  label="Avg Viewed %"
                />
                <StatCard
                  icon={<ScoreIcon />}
                  value={formatSeconds(item.average_viewed_duration_sec)}
                  label="Avg Duration"
                />
                <StatCard
                  icon={<ScoreIcon />}
                  value={safeText(item.top_5_countries)}
                  label="Top 5 Countries"
                />

                <StatCard
                  icon={<ScoreIcon />}
                  value={safeText(item.mode_agerange)}
                  label="Mode Age Range"
                />
                <StatCard
                  icon={<ScoreIcon />}
                  value={safeText(item.Female)}
                  label="Female"
                />
                <StatCard
                  icon={<ScoreIcon />}
                  value={safeText(item.Male)}
                  label="Male"
                />
                <StatCard
                  icon={<ScoreIcon />}
                  value={safeText(item.PreferNotToDisclose)}
                  label="Prefer Not To Disclose"
                />
              </Box>

              {idx < categoryDataList.length - 1 ? (
                <Divider sx={{ my: 3.5, borderColor: "#e5e7eb" }} />
              ) : null}
            </Box>
          ))}
        </Box>
      </Box>
    </Modal>
  );
};

export default OmneInsightsModal;